import type { InputLabelProps } from '@mui/material';
import {
  FormControl as MuiFormControl,
  FormHelperText,
  InputLabel,
  Skeleton,
  styled,
  useTheme,
} from '@mui/material';
import { forwardRef, useState } from 'react';

import { typographyToSystem } from '../../theme/typography';
import { ChkMaskedInput } from '../ChkMaskedInput/ChkMaskedInput';
import { ChkIconWarning } from '../icons';

import type { ChkInputGroupProps } from './ChkInputGroup.types';

export const ChkInputGroup = forwardRef<HTMLInputElement, ChkInputGroupProps>(
  (
    {
      label,
      testId,
      fieldName,
      helpText,
      helpTextId,
      errorText,
      errorTextId,
      fullWidth,
      error,
      variant = 'regular',
      onBlur,
      mask,
      validateUntouched,
      placeholder,
      isLoading = false,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();
    const isZoomed = theme.zoom.isIntermediateZoom;
    const zoomedHelpText = isZoomed ? helpText || placeholder : helpText;
    const zoomedPlaceholder = isZoomed ? undefined : placeholder;
    const [isInputTouched, setIsInputTouched] = useState(false);
    const displayError =
      error && Boolean(errorText) && (validateUntouched || isInputTouched);

    const describedBy = [helpTextId, errorTextId].filter(Boolean).join(' ');

    const onInputBlur = (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
    ) => {
      setIsInputTouched(true);
      onBlur?.(event);
    };

    const onInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setIsInputTouched(false);

      if ('onChange' in rest) {
        rest.onChange?.(event?.currentTarget.value);
      }
    };
    return (
      <FormControl
        fullWidth={fullWidth}
        data-field-name={fieldName || undefined}
        data-testid={`${testId}-form-control`}
      >
        <StyledInputLabel htmlFor={testId} shrink>
          {label}
        </StyledInputLabel>
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            sx={{ borderRadius: '0.8rem', width: '100%', height: '4.8rem' }}
            data-testid={`${testId}-skeleton`}
          />
        ) : (
          <ChkMaskedInput
            showPromptOnInput
            inputRef={ref}
            id={testId}
            inputProps={{ 'data-testid': testId }}
            aria-describedby={describedBy}
            aria-invalid={displayError}
            error={displayError}
            mask={mask}
            onBlur={onInputBlur}
            placeholder={zoomedPlaceholder}
            {...rest}
            onChange={onInputChange}
          />
        )}
        {!displayError && zoomedHelpText && (
          <StyledFormHelperText id={helpTextId} data-testid={helpTextId}>
            {zoomedHelpText}
          </StyledFormHelperText>
        )}
        {displayError && (
          <StyledFormErrorText id={errorTextId} data-testid={errorTextId}>
            <IconWrapper>
              <ChkIconWarning size="sm" />
            </IconWrapper>
            {errorText}
          </StyledFormErrorText>
        )}
      </FormControl>
    );
  }
);

const FormControl = styled(MuiFormControl)({
  position: 'relative',
});

const StyledInputLabel = styled(InputLabel)<InputLabelProps>(({ theme }) => ({
  ...typographyToSystem(theme.typography.body3),
  color: theme.tokens.inputValueActive,
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(2),
  position: 'static',
  whiteSpace: 'wrap',
  transform: 'none',
  transition: 'none',

  '&.Mui-focused': {
    color: theme.tokens.inputValueActive,
  },
}));

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
  margin: theme.spacing(2, 2, 0, 2),
  color: theme.tokens.inputValueHelpDefault,
  ...typographyToSystem(theme.typography.body3),
}));

const StyledFormErrorText = styled(FormHelperText)(({ theme }) => ({
  margin: theme.spacing(2, 2, 0, 2),
  color: theme.tokens.inputValueHelpError,
  ...typographyToSystem(theme.typography.body3),
}));

const IconWrapper = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  transform: `translateY(${theme.spacing(2)})`,
  marginRight: theme.spacing(2),
  alignItems: 'center',
}));
